'use client'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@teqplay/ui'
import { useTranslations, useLocale } from 'next-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faChevronDown, faGlobe } from '@fortawesome/pro-solid-svg-icons'

import { usePathname, useRouter } from '@/i18n/navigation'
import {
  getLocalizationLockInfo,
  getPortConfig,
  LOCALIZE_VERSION,
  PORTS,
  TLocalizeVersion
} from '@/app/config/ports'
import { useSessionStorage } from '@/app/hooks/useSessionStorage'
import { STORAGE_KEY } from '@/app/constants'
import { useEffectiveLocalizeVersion } from '@/app/hooks/useEffectiveLocalizeVersion'

// This action section is used on simple rebranding to teqplay which remove the capability of auth and user profiles dropdown

function GlobalPortSelection() {
  const t = useTranslations()
  const pathname = usePathname()
  const router = useRouter()
  const localizeVersion = useEffectiveLocalizeVersion()

  // User-selectable localization preference stored in sessionStorage
  const [selectedLocalize, setSelectedLocalize] = useSessionStorage<TLocalizeVersion | null>(
    STORAGE_KEY.SELECTED_LOCALIZE_VERSION,
    null
  )
  const currentLocalizeForDisplay = (selectedLocalize as TLocalizeVersion) || localizeVersion
  const localizeDisplay =
    currentLocalizeForDisplay === LOCALIZE_VERSION.GLOBAL
      ? LOCALIZE_VERSION.SANTOS
      : currentLocalizeForDisplay

  const localizationLockInfo =
    typeof window !== 'undefined'
      ? getLocalizationLockInfo(window.location.hostname)
      : { isLocked: false, lockedPort: null }
  const isLocalizationLocked = localizationLockInfo.isLocked
  const localizationButtonTitle = localizationLockInfo.lockedPort
    ? t('Sidebar.lockedByDomain', {
        portName: t(`localization.label.${localizationLockInfo.lockedPort.translationKey}`)
      })
    : undefined
  // Get all port slugs dynamically for the regex pattern
  const allPortSlugs = Object.values(PORTS)
    .map((p) => p.slug)
    .join('|')

  const onLocalizationChange = (newLocalize: TLocalizeVersion) => {
    setSelectedLocalize(newLocalize)
    setTimeout(() => {
      // Get the port slug from the PORTS configuration
      const portConfig = getPortConfig(newLocalize)
      const desiredPortSlug = portConfig?.slug ?? null

      // Only apply port-based redirects when switching explicitly to a port
      if (desiredPortSlug && pathname) {
        const portPattern = new RegExp(`^/(${allPortSlugs})/(ships-at-port|ships-expected)(/.*)?$`)
        const match = pathname.match(portPattern)

        // Only redirect if we are on one of the two ships pages
        if (match) {
          const currentPortSlug = match[1]
          const shipsPageSlug = match[2] // "ships-at-port" or "ships-expected"
          const rest = match[3] ?? ''

          // Redirect to the same ships page type but with the newly selected port
          if (currentPortSlug !== desiredPortSlug) {
            const nextPathname = `/${desiredPortSlug}/${shipsPageSlug}${rest}`
            router.push(nextPathname)
            return
          }
        }
      }

      window.location.reload()
    }, 100)
  }

  return (
    <ul className="action-section-ul">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isLocalizationLocked}>
          <span
            className="button link size-m"
            style={{ cursor: isLocalizationLocked ? 'not-allowed' : 'pointer' }}
            title={localizationButtonTitle}
            aria-disabled={isLocalizationLocked}
          >
            <FontAwesomeIcon icon={faGlobe} />
            {t(`localization.label.${localizeDisplay}`)}
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="dropdown-language" align="end">
          {/* Dynamically generated port options */}
          {Object.values(PORTS).map((port) => (
            <DropdownMenuItem
              key={port.key}
              onClick={() => onLocalizationChange(port.key as TLocalizeVersion)}
            >
              {currentLocalizeForDisplay === port.key && <FontAwesomeIcon icon={faCheck} />}{' '}
              {t(`localization.label.${port.translationKey}`)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </ul>
  )
}

export default GlobalPortSelection
